// Intercom integration and collision avoidance -
// Manages positioning of floating UI components to avoid overlapping with Intercom widget.
// (The actual lifecycle of Intercom is handled via `intercom-rails` gem - `intercom_script_tag`)

import { breakpoints } from "./breakpoint_events";
import { isVisible, pollUntil } from "./utils";
import { debounce } from "lodash-es";

const INTERCOM_LAUNCHER_SELECTOR = ".intercom-lightweight-app-launcher"; // It's the "lightweight" one upon load, until clicked
const INTERCOM_MESSENGER_SELECTOR = ".intercom-messenger-frame";
const INTERCOM_CONTAINER_SELECTOR = "#intercom-container";

const FLOATING_ID = "floating-component";
const BOTTOM_BANNER_ID = "account-upgrade-banner";
const HAS_ACCOUNT_UPGRADE_BANNER = "has-account-upgrade-banner";
const INVENTORY_VIEW_CLASS = "inventory-view";

const SMALL_MOBILE_MAX_WIDTH = 480;
const MOBILE_MAX_WIDTH = 768;
const MARGIN = 8;
const INTERCOM_DEFAULT_LAUNCHER_PX = 48;
const FLOATING_OFFSET_HORIZONTAL_PX = 40;
const FLOATING_OFFSET_VERTICAL_PX = 80;
const REPOSITION_DEBOUNCE_MS = 50;

// Module-level state
let intercomPollCancel = null;
let isRepositioning = false;

const getIntercomLauncherButton = () => {
  const selectors = [
    INTERCOM_LAUNCHER_SELECTOR,
    ".intercom-launcher",
    `${INTERCOM_CONTAINER_SELECTOR} iframe[name*="launcher"]`,
  ];

  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) return el;
  }
  return null;
};

const adjustFloatingForIntercom = (floatingButton) => {
  if (!floatingButton) return;

  // If Intercom isn't configured for this page, don't reposition
  const chatProvider = document.body.dataset.chatProvider;
  if (chatProvider !== "intercom") return;

  const intercomLauncherButton = getIntercomLauncherButton();
  const connected =
    !!intercomLauncherButton &&
    (intercomLauncherButton.isConnected || document.contains(intercomLauncherButton));

  let launcherWidth = 0;
  if (connected) {
    launcherWidth =
      intercomLauncherButton.offsetWidth ||
      intercomLauncherButton.getBoundingClientRect()?.width ||
      0;
  }

  if (!Number.isFinite(launcherWidth) || launcherWidth <= 0) {
    launcherWidth = INTERCOM_DEFAULT_LAUNCHER_PX;
  }

  // For non-inventory views with banner, use standard mobile breakpoint (768px)
  // to ensure floating button is above chat on all mobile devices, avoiding banner overlap.
  // On inventory views there won't be a banner, and we want to show the floating button
  // to the side instead of on top - until screen is so small that the component wouldn't fit
  // when opened (that's the SMALL_MOBILE_MAX_WIDTH thing).
  const isInventoryView = document.body.classList.contains(INVENTORY_VIEW_CLASS);
  const hasBanner = document.body.classList.contains(HAS_ACCOUNT_UPGRADE_BANNER);
  const mobileThreshold = !isInventoryView && hasBanner ? MOBILE_MAX_WIDTH : SMALL_MOBILE_MAX_WIDTH;

  const isWideViewport = window.innerWidth > mobileThreshold;

  if (isWideViewport) {
    const rightOffset = launcherWidth + FLOATING_OFFSET_HORIZONTAL_PX;

    floatingButton.style.setProperty("right", rightOffset + "px", "important");
    floatingButton.style.removeProperty("bottom");

    const intercomLauncher = document.querySelector(INTERCOM_LAUNCHER_SELECTOR);
    const intercomContainer = document.querySelector(INTERCOM_CONTAINER_SELECTOR);
    if (intercomLauncher) intercomLauncher.style.removeProperty("bottom");
    if (intercomContainer) intercomContainer.style.removeProperty("bottom");
  } else {
    // Intercom's code fights repositioning attempts, so we move our button instead
    floatingButton.style.setProperty("bottom", `${FLOATING_OFFSET_VERTICAL_PX}px`, "important");
    floatingButton.style.removeProperty("right");
  }
};

const positionFloatingForBanner = ({ floatingButton, bottomBanner, intercomOpen }) => {
  if (!floatingButton || !breakpoints["mobile"]) return;

  floatingButton.style.bottom = "";
  if (isVisible(bottomBanner) && !intercomOpen) {
    const bannerHeight = bottomBanner.offsetHeight;
    if (bannerHeight > 0) {
      floatingButton.style.bottom = bannerHeight + MARGIN + "px";
    }
  }
};

const repositionToAvoidOverlapping = debounce(
  () => {
    if (isRepositioning) return;

    isRepositioning = true;

    try {
      const floatingButton = document.getElementById(FLOATING_ID);
      const bottomBanner = document.getElementById(BOTTOM_BANNER_ID);
      const intercomLauncher = document.querySelector(INTERCOM_LAUNCHER_SELECTOR);
      const intercomMessenger = document.querySelector(INTERCOM_MESSENGER_SELECTOR);
      const intercom = intercomLauncher || intercomMessenger;

      if (!intercom && !floatingButton && !bottomBanner) return;

      const intercomOpen = !!intercomMessenger;
      positionFloatingForBanner({ floatingButton, bottomBanner, intercomOpen });

      adjustFloatingForIntercom(floatingButton);
    } finally {
      isRepositioning = false;
    }
  },
  REPOSITION_DEBOUNCE_MS,
  { leading: true, trailing: true }
);

const waitForIntercom = () => {
  const tryAttach = () => {
    const intercomLauncher = document.querySelector(INTERCOM_LAUNCHER_SELECTOR);
    const intercomMessenger = document.querySelector(INTERCOM_MESSENGER_SELECTOR);
    const intercomWidget = intercomLauncher || intercomMessenger;

    if (intercomWidget) {
      repositionToAvoidOverlapping();
      return true;
    }
    return false;
  };

  if (tryAttach()) return;

  if (!intercomPollCancel) {
    intercomPollCancel = pollUntil({
      check: tryAttach,
      interval: 200,
      maxAttempts: 50,
      onGiveUp: () => {
        intercomPollCancel = null;
      },
    });
  }
};

const bindRepositioning = () => {
  document.addEventListener("partial:replace", repositionToAvoidOverlapping);
  window.addEventListener("resize", repositionToAvoidOverlapping);

  waitForIntercom();
};

const initializeIntercom = () => {
  bindRepositioning();
  requestAnimationFrame(() => repositionToAvoidOverlapping());
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeIntercom);
} else {
  initializeIntercom();
}
